import React, { useState, useEffect } from "react";
import axios from "axios";

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  where,
  query,
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const Scraper = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [legalAreas, setLegalAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [lawsList, setLawsList] = useState([]);
  const [lawDetails, setLawDetails] = useState(null);
  const [error, setError] = useState(null);
  const [scrapingStatus, setScrapingStatus] = useState("idle");
  const [maxPages, setMaxPages] = useState(100);
  const [indexingStatus, setIndexingStatus] = useState(null);
  const [indexingResult, setIndexingResult] = useState(null);
  const [savedLaws, setSavedLaws] = useState([]);

  const navigate = useNavigate();

  // Add this effect at the top of your existing useEffect blocks
  useEffect(() => {
    // Check if user is not admin and redirect
    if (!user.admin) {
      navigate("/");
    }
  }, [navigate]);

  const fetchSavedLaws = async () => {
    try {
      const lawsCollection = collection(db, "laws");
      const snapshot = await getDocs(lawsCollection);
      const lawsList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSavedLaws(lawsList);
    } catch (error) {
      console.error("Error fetching laws:", error);
      setError("Kunde inte hämta sparade lagar: " + error.message);
    }
  };

  // Befintliga metoder för scraping
  const scrapeMainPage = async () => {
    try {
      setLoading(true);
      setScrapingStatus("Hämtar rättsområden...");

      const response = await axios.post(`${API_URL}/api/scrape`, {
        url: "https://www.lagboken.se/Lagboken/start/",
        selector: ".c-tag-list__item a",
        type: "areas",
      });

      setLegalAreas(response.data);
      setScrapingStatus(`Hittade ${response.data.length} rättsområden`);
    } catch (err) {
      setError(`Fel vid scraping av rättsområden: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const scrapeLawsForArea = async (area) => {
    try {
      setSelectedArea(area);
      setLawsList([]);
      setLawDetails(null);
      setLoading(true);
      setScrapingStatus(`Hämtar lagar för ${area.name}...`);

      const response = await axios.post(`${API_URL}/api/scrape`, {
        url: `https://www.lagboken.se${area.url}`,
        selector: "#paging-list .c-list__item a",
        type: "laws",
        maxPages: maxPages,
      });

      setLawsList(response.data);
      setScrapingStatus(
        `Hittade totalt ${response.data.length} lagar för ${area.name}`,
      );
    } catch (err) {
      setError(`Fel vid scraping av lagar: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const scrapeLawPage = async (law) => {
    try {
      setLawDetails(null);
      setLoading(true);
      setScrapingStatus(`Hämtar detaljer för ${law.name}...`);

      const response = await axios.post(`${API_URL}/api/scrape`, {
        url: `https://www.lagboken.se${law.url}`,
        type: "law-details",
      });

      setLawDetails(response.data);
      setScrapingStatus(`Hämtat detaljer för ${law.name}`);
    } catch (err) {
      setError(`Fel vid scraping av lagdetaljer: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // NY FUNKTION: Indexera lag till Pinecone
  const indexLawToPinecone = async () => {
    if (!lawDetails) {
      setError("Ingen lag vald för indexering");
      return;
    }

    try {
      setIndexingStatus("Förbereder indexering...");
      setLoading(true);

      // Dela upp lagtexten i chunks
      const paragraphs = lawDetails.lawText.split(/\n\s*\n/);
      const chunks = [];
      let currentChunk = "";
      const chunkSize = 1000;

      for (const paragraph of paragraphs) {
        if (currentChunk.length + paragraph.length + 2 > chunkSize) {
          chunks.push(currentChunk);
          currentChunk = paragraph;
        } else {
          currentChunk += (currentChunk ? "\n\n" : "") + paragraph;
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk);
      }

      // Skapa säkert SFS-nummer för ID
      const safeSfsNumber = lawDetails.sfsNumber
        ? lawDetails.sfsNumber
            .replace(/\s+/g, "-")
            .replace(/[\/\\:*?"<>|]/g, "-")
        : `law-${Date.now()}`;

      // Skapa dokument för indexering i samma format som i DocumentUploader
      const documents = chunks.map((chunk, index) => ({
        text: chunk,
        source: `${lawDetails.title} (del ${index + 1})`,
        id: `lagboken-${safeSfsNumber}-${index}`,
        position: index,
      }));

      // Metadata i samma format som i DocumentUploader
      const metadata = {
        namespace: "lagboken",
        law: lawDetails.title,
        contentType: "legislation",
        totalChunks: chunks.length,
        sfsNumber: lawDetails.sfsNumber,
        issued: lawDetails.issued,
        department: lawDetails.department,
        lastUpdated: lawDetails.lastUpdated,
        area: selectedArea.name,
        url: lawDetails.url,
      };

      setIndexingStatus("Indexerar lag till Pinecone...");

      // Använd samma endpoints som DocumentUploader
      const response = await axios.post(`${API_URL}/api/documents/upload`, {
        documents,
        metadata,
      });

      setIndexingResult({
        lawName: lawDetails.title,
        chunks: chunks.length,
        message: response.data.message || "Indexeringen lyckades",
      });

      setIndexingStatus("Indexering slutförd");
    } catch (err) {
      console.error("Indexering misslyckades:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Ett fel uppstod vid indexeringen",
      );
      setIndexingStatus("Indexering misslyckades");
    } finally {
      setLoading(false);
    }
  };

  const saveLawToFirestore = async (lawData, areaName) => {
    try {
      // Create a document reference with a collection name "laws"
      const lawsCollection = collection(db, "laws");

      // Structure the law data for Firestore
      const lawDocument = {
        title: lawData.title,
        sfsNumber: lawData.sfsNumber,
        issued: lawData.issued,
        department: lawData.department,
        lastUpdated: lawData.lastUpdated,
        description: lawData.description,
        area: areaName,
        url: lawData.url,
        lawText: lawData.lawText,
        createdAt: new Date(),
      };

      // Add the document to Firestore
      const docRef = await addDoc(lawsCollection, lawDocument);
      console.log(
        `Law ${lawData.title} added to Firestore with ID: ${docRef.id}`,
      );

      return { success: true, docId: docRef.id };
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      return { success: false, error: error.message };
    }
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <h1>Lagboken.se Scraper & Indexering</h1>

      <div style={{ marginBottom: "20px" }}>
        <h2>Sparade Lagar</h2>
        <button
          onClick={fetchSavedLaws}
          style={{
            padding: "8px 16px",
            backgroundColor: "#9c27b0",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            marginBottom: "10px",
          }}
        >
          Visa sparade lagar
        </button>

        {savedLaws.map((law) => (
          <div
            key={law.id}
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
              padding: "10px",
              marginBottom: "10px",
            }}
          >
            <h3>{law.title}</h3>
            <p>
              <strong>SFS:</strong> {law.sfsNumber}
            </p>
            <details>
              <summary style={{ cursor: "pointer" }}>Visa lagtext</summary>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  borderRadius: "4px",
                }}
              >
                {law.lawText}
              </pre>
            </details>
          </div>
        ))}
      </div>
      {error && (
        <div
          style={{
            padding: "10px",
            marginBottom: "10px",
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "4px",
          }}
        >
          <p style={{ color: "#d32f2f" }}>{error}</p>
          <button
            onClick={() => setError(null)}
            style={{
              padding: "5px 10px",
              border: "1px solid #d32f2f",
              color: "#d32f2f",
              backgroundColor: "transparent",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Stäng
          </button>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "20px",
          padding: "10px",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
        }}
      >
        <button
          onClick={scrapeMainPage}
          disabled={loading}
          style={{
            marginRight: "10px",
            padding: "8px 16px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          Hämta Rättsområden
        </button>

        <span style={{ color: "#757575" }}>{scrapingStatus}</span>

        {loading && <span style={{ marginLeft: "10px" }}>Laddar...</span>}
      </div>

      {/* NY SEKTION: Indexeringsstatus */}
      {indexingStatus && (
        <div
          style={{
            marginBottom: "20px",
            padding: "10px",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            backgroundColor: "#f5f5f5",
          }}
        >
          <h3 style={{ margin: "0 0 10px 0" }}>Indexeringsstatus:</h3>
          <p>{indexingStatus}</p>

          {indexingResult && (
            <div
              style={{
                backgroundColor: "#e8f5e9",
                padding: "10px",
                borderRadius: "4px",
                marginTop: "10px",
              }}
            >
              <h4 style={{ margin: "0 0 5px 0" }}>Resultat:</h4>
              <p style={{ margin: "5px 0" }}>{indexingResult.message}</p>
              {indexingResult.lawName && (
                <p style={{ margin: "5px 0" }}>
                  <strong>Lag:</strong> {indexingResult.lawName}
                </p>
              )}
              {indexingResult.chunks && (
                <p style={{ margin: "5px 0" }}>
                  <strong>Antal chunks:</strong> {indexingResult.chunks}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {legalAreas.length > 0 && (
          <div style={{ flex: "1", minWidth: "300px" }}>
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              <h2 style={{ margin: "0 0 10px 0" }}>
                Rättsområden ({legalAreas.length})
              </h2>
              <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {legalAreas.map((area) => (
                    <li
                      key={area.id}
                      onClick={() => scrapeLawsForArea(area)}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                        backgroundColor:
                          selectedArea && selectedArea.id === area.id
                            ? "#e3f2fd"
                            : "transparent",
                        borderRadius: "4px",
                      }}
                    >
                      {area.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {lawsList.length > 0 && (
          <div
            style={{
              flex: selectedArea && lawDetails ? "1" : "2",
              minWidth: "300px",
            }}
          >
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              <h2 style={{ margin: "0 0 10px 0" }}>
                Lagar för {selectedArea?.name} ({lawsList.length})
              </h2>

              <div style={{ marginBottom: "10px" }}>
                <span style={{ color: "#757575" }}>Max sidor att hämta:</span>
                {[1, 2, 5, 0].map((num) => (
                  <button
                    key={num}
                    onClick={() => setMaxPages(num)}
                    style={{
                      margin: "0 5px",
                      padding: "5px 10px",
                      backgroundColor:
                        maxPages === num ? "#1976d2" : "transparent",
                      color: maxPages === num ? "white" : "#1976d2",
                      border: "1px solid #1976d2",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {num === 0 ? "Alla" : num}
                  </button>
                ))}
              </div>
              {/* Bulk indexing button */}
              <div style={{ marginBottom: "10px" }}>
                <button
                  onClick={async () => {
                    if (!selectedArea) {
                      setError("Inget rättsområde valt");
                      return;
                    }

                    setLoading(true);
                    setIndexingStatus(
                      `Förbereder komplett indexering av ${selectedArea.name}...`,
                    );

                    try {
                      // First fetch ALL laws for this area by setting maxPages to 0
                      setScrapingStatus(
                        `Hämtar alla lagar för ${selectedArea.name}...`,
                      );

                      const lawsResponse = await axios.post(
                        `${API_URL}/api/scrape`,
                        {
                          url: `https://www.lagboken.se${selectedArea.url}`,
                          selector: "#paging-list .c-list__item a",
                          type: "laws",
                          maxPages: 0, // This ensures we get ALL pages
                        },
                      );

                      const allLaws = lawsResponse.data;
                      setScrapingStatus(
                        `Hittade totalt ${allLaws.length} lagar för ${selectedArea.name}`,
                      );

                      // Now process each law
                      let successful = 0;
                      let failed = 0;

                      const lawsToProcess = allLaws.slice(0, 2);

                      for (const law of allLaws) {
                        try {
                          setScrapingStatus(
                            `Hämtar detaljer för ${law.name}...`,
                          );

                          // Fetch law details
                          const response = await axios.post(
                            `${API_URL}/api/scrape`,
                            {
                              url: `https://www.lagboken.se${law.url}`,
                              type: "law-details",
                            },
                          );

                          const lawData = response.data;

                          // Process and index the law
                          setIndexingStatus(`Indexerar ${law.name}...`);

                          // Dela upp lagtexten i chunks
                          const paragraphs = lawData.lawText.split(/\n\s*\n/);
                          const chunks = [];
                          let currentChunk = "";
                          const chunkSize = 1000;

                          for (const paragraph of paragraphs) {
                            if (
                              currentChunk.length + paragraph.length + 2 >
                              chunkSize
                            ) {
                              chunks.push(currentChunk);
                              currentChunk = paragraph;
                            } else {
                              currentChunk +=
                                (currentChunk ? "\n\n" : "") + paragraph;
                            }
                          }

                          if (currentChunk) {
                            chunks.push(currentChunk);
                          }

                          // Skapa säkert SFS-nummer för ID
                          const safeSfsNumber = lawData.sfsNumber
                            ? lawData.sfsNumber
                                .replace(/\s+/g, "-")
                                .replace(/[\/\\:*?"<>|]/g, "-")
                            : `law-${Date.now()}`;

                          // Skapa dokument för indexering
                          const documents = chunks.map((chunk, index) => ({
                            text: chunk,
                            source: `${lawData.title} (del ${index + 1})`,
                            id: `lagboken-${safeSfsNumber}-${index}`,
                            position: index,
                          }));

                          // Metadata
                          const metadata = {
                            namespace: "lagboken",
                            law: lawData.title,
                            contentType: "legislation",
                            totalChunks: chunks.length,
                            sfsNumber: lawData.sfsNumber,
                            issued: lawData.issued,
                            department: lawData.department,
                            lastUpdated: lawData.lastUpdated,
                            area: selectedArea.name,
                            url: lawData.url,
                          };

                          // Indexering
                          await axios.post(`${API_URL}/api/documents/upload`, {
                            documents,
                            metadata,
                          });

                          successful++;
                        } catch (err) {
                          console.error(
                            `Fel vid indexering av ${law.name}:`,
                            err,
                          );
                          failed++;
                        }

                        setIndexingStatus(
                          `Framsteg: ${successful + failed}/${allLaws.length} (${successful} lyckade, ${failed} misslyckade)`,
                        );
                      }

                      setIndexingResult({
                        lawName: `${selectedArea.name} - alla lagar`,
                        chunks: successful,
                        message: `Indexering slutförd. ${successful} lagar indexerade, ${failed} misslyckades.`,
                      });
                    } catch (err) {
                      setError(`Fel vid bulk-indexering: ${err.message}`);
                    } finally {
                      setIndexingStatus("Bulk-indexering slutförd");
                      setLoading(false);
                    }
                  }}
                  disabled={loading || !selectedArea}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#4caf50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      loading || !selectedArea ? "not-allowed" : "pointer",
                    opacity: loading || !selectedArea ? 0.7 : 1,
                  }}
                >
                  Indexera ALLA lagar i {selectedArea?.name}
                </button>
              </div>
              {selectedArea && lawsList[0]?.areaDescription && (
                <div
                  style={{
                    padding: "10px",
                    marginBottom: "10px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <p style={{ margin: "0" }}>{lawsList[0].areaDescription}</p>
                </div>
              )}

              <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {lawsList.map((law) => (
                    <li
                      key={law.id}
                      onClick={() => scrapeLawPage(law)}
                      style={{
                        padding: "8px 10px",
                        cursor: "pointer",
                        backgroundColor:
                          lawDetails && lawDetails.title === law.name
                            ? "#e3f2fd"
                            : "transparent",
                        borderRadius: "4px",
                      }}
                    >
                      {law.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {lawDetails && (
          <div style={{ flex: "1", minWidth: "300px" }}>
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              <h2 style={{ margin: "0 0 10px 0" }}>{lawDetails.title}</h2>

              {/* NY KNAPP: För indexering till Pinecone */}
              <div style={{ marginBottom: "15px" }}>
                <button
                  onClick={indexLawToPinecone}
                  disabled={loading}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#4caf50",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                  }}
                >
                  Indexera i Pinecone
                </button>
              </div>

              <p style={{ margin: "5px 0" }}>
                <strong>SFS Nummer:</strong> {lawDetails.sfsNumber}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Utfärdad:</strong> {lawDetails.issued}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Departement:</strong> {lawDetails.department}
              </p>
              <p style={{ margin: "5px 0" }}>
                <strong>Senast uppdaterad:</strong> {lawDetails.lastUpdated}
              </p>

              <hr
                style={{
                  margin: "15px 0",
                  border: "none",
                  borderTop: "1px solid #e0e0e0",
                }}
              />

              <p style={{ margin: "10px 0" }}>
                <strong>Beskrivning:</strong> {lawDetails.description}
              </p>

              <div style={{ marginTop: "15px" }}>
                <details>
                  <summary
                    style={{
                      cursor: "pointer",
                      padding: "8px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                    }}
                  >
                    Visa Lagtext
                  </summary>
                  <div
                    style={{
                      maxHeight: "400px",
                      overflow: "auto",
                      padding: "10px",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "4px",
                      marginTop: "10px",
                    }}
                  >
                    <pre style={{ whiteSpace: "pre-wrap" }}>
                      {lawDetails.lawText}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Scraper;
